<template>
    <v-container fluid>
        <!-- Top toolbar -->
        <v-row align="center" class="mb-2">
            <v-col cols="12" sm="4" class="pl-7">
                <Dropdown
                    v-model="selectedMaritimeObject"
                    :ajax="true"
                    :data="'maritime-objects'"
                    :label="$t('fields.maritimeObject')"
                />
            </v-col>

            <v-col class="text-right" cols="auto">
                <v-btn color="primary" @click="toggleChangeStatus">
                    <v-icon small>{{ changeStatusActive ? 'fa-lock' : 'fa-edit' }}</v-icon>
                    &nbsp;{{ changeStatusActive ? $t('buttons.lock') : $t('buttons.edit') }}
                </v-btn>
            </v-col>

            <v-col v-if="changeStatusActive" class="text-right pr-0" cols="auto">
                <v-btn color="primary" @click="applyChangesClick">
                    {{ $t('buttons.changeStatus') }}
                </v-btn>
            </v-col>

            <v-col class="text-right" cols="auto">
                <v-btn color="secondary" @click="bulkPlanClick">
                    {{ $t('buttons.bulkPlan') }}
                </v-btn>
            </v-col>
        </v-row>

        <!-- Kanban columns -->
        <v-row dense wrap>
            <!--
                "Planned & Joining" is rendered separately because it combines
                two different data sources (no-contract + planned) and has no checkbox.
            -->
            <v-col>
                <v-card height="80vh">
                    <v-card-title class="collTitle pt-4">
                        {{ $t('labels.plannedAndJoining') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-row>
                            <!-- No-contract placements (white background) -->
                            <v-col
                                v-for="placement in crewListDataPlacementsNoContract"
                                :key="'nc-' + placement.id"
                                cols="12"
                                class="py-0 mb-1"
                            >
                                <placement-card
                                    :placement="placement"
                                    :date-label="$t('labels.effectiveFrom')"
                                    :show-checkbox="changeStatusActive"
                                    background-color="#FFFFFF"
                                    @toggle-select="addSeafarerToChangeStatusList"
                                    @open="openPlacement"
                                />
                            </v-col>

                            <!-- Planned placements (yellow background, no checkbox) -->
                            <v-col
                                v-for="placement in crewListDataPlannedPlacements"
                                :key="'pl-' + placement.id"
                                cols="12"
                                class="py-0 mb-1"
                            >
                                <placement-card
                                    :placement="placement"
                                    :date-label="$t('labels.plannedSignOn')"
                                    :show-checkbox="false"
                                    background-color="#FFE599"
                                    @open="openPlacement"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Dynamic status columns (Travel to Vessel, Training, Sick Leave, On Board, Travel from Vessel) -->
            <v-col v-for="col in statusColumns" :key="col.statusId">
                <v-card height="80vh">
                    <v-card-title class="collTitle pt-4">{{ col.label }}</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-row>
                            <v-col
                                v-for="placement in placementsByStatus(col.statusId)"
                                :key="placement.id"
                                cols="12"
                                class="py-0 mb-1"
                            >
                                <placement-card
                                    :placement="placement"
                                    :date-label="$t('labels.since')"
                                    :show-checkbox="changeStatusActive"
                                    :background-color="col.color"
                                    :show-pending="true"
                                    @toggle-select="addSeafarerToChangeStatusList"
                                    @open="openPlacement"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Off-board column -->
            <v-col>
                <v-card height="80vh">
                    <v-card-title class="collTitle">
                        <Dropdown
                            v-model="selectedOffboard"
                            :ajax="false"
                            :data="offboardSelect"
                            :label="$t('fields.offBoard')"
                            class="pa-0 ma-0"
                        />
                    </v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-row>
                            <v-col
                                v-for="placement in crewListOffBoard"
                                :key="placement.id"
                                cols="12"
                                class="py-0 mb-1"
                            >
                                <PlacementCard
                                    :placement="placement"
                                    :date-label="$t('labels.offBoard')"
                                    date-field="end_date"
                                    :show-checkbox="false"
                                    background-color="#FFFFFF"
                                    @open="openPlacement"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- ---------------------------------------------------------------- -->
        <!-- Dialogs                                                           -->
        <!-- ---------------------------------------------------------------- -->

        <!-- Bulk Plan -->
        <v-dialog v-model="bulkPlanDialog" width="600" persistent>
            <v-card>
                <v-card-title>{{ $t('labels.planMultipleSeafarers') }}</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12">
                            <DropdownMultiple
                                v-model="bulkPlanSeafarers"
                                :ajax="false"
                                :data="availableSeafarersForBulkPlan"
                                :label="$t('fields.seafarer')"
                                :key="componentKey"
                            />
                        </v-col>
                        <v-col cols="12">
                            <DatePicker
                                v-model="bulkPlanDate"
                                :readonly="false"
                                :date-label="$t('fields.plannedSignOn')"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6" class="text-right">
                            <v-btn color="primary" class="white--text" @click.stop="bulkPlanDialogClick">
                                {{ $t('buttons.bulkPlan') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="6" class="text-left">
                            <v-btn color="secondary" class="white--text" @click.stop="closeBulkPlanDialog">
                                {{ $t('buttons.cancel') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Change Status: pick status + date -->
        <v-dialog v-model="changeStatusDialog" width="300">
            <v-card>
                <v-card-title>{{ $t('labels.bulkChangeStatus') }}</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12">
                            <Dropdown
                                v-model="changeStatusTo"
                                :ajax="true"
                                :data="'seafarer-statuses'"
                                :label="$t('fields.status')"
                            />
                        </v-col>
                        <v-col cols="12" v-if="changeStatusTo === 1">
                            <Dropdown
                                v-model="changeStatusInactiveReason"
                                :ajax="true"
                                :data="'inactive-reasons'"
                                :label="'* ' + $t('fields.reason')"
                            />
                        </v-col>
                        <v-col cols="12">
                            <DatePicker
                                v-model="changeStatusDate"
                                :readonly="false"
                                :date-label="$t('fields.onDate')"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="6" class="text-right">
                            <v-btn color="primary" class="white--text" @click.stop="confirmChangeStatusClick">
                                {{ $t('buttons.confirm') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="6" class="text-left">
                            <v-btn color="secondary" class="white--text" @click.stop="closeChangeStatusDialog">
                                {{ $t('buttons.cancel') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Change Status: verify -->
        <v-dialog v-model="changeStatusVerifyDialog" width="700">
            <v-card>
                <v-card-title> {{ $t('labels.changeStatusCrewlistLabel') }} {{ dateForVerifyModal }} </v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col v-for="placement in selectedSeafarersForChangeStatus" :key="placement.id" cols="12">
                            <v-icon small color="secondary" class="pl-2">fas fa-pause-circle</v-icon>
                            <b>{{ placement.seafarer.name }} {{ placement.seafarer.surname }}</b>
                            &nbsp;{{ placement.activity_display }}&nbsp;
                            <v-icon small color="black" class="pl-2">fas fa-arrow-right</v-icon>
                            &nbsp;{{ statusToChange }}
                        </v-col>
                    </v-row>
                    <v-row class="mt-2">
                        <v-col cols="12" md="6" class="text-right">
                            <v-btn color="primary" class="white--text" @click.stop="confirmChangeStatusVerifyDialog">
                                {{ $t('buttons.confirm') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="6" class="text-left">
                            <v-btn color="secondary" class="white--text" @click.stop="closeChangeStatusVerifyDialog">
                                {{ $t('buttons.cancel') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Change Status: results -->
        <v-dialog v-model="changeStatusResponseDialog" width="900">
            <v-card>
                <v-card-title> {{ $t('labels.reportForActionsOn') }} {{ dateForVerifyModal }} </v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col v-for="placement in selectedSeafarersForChangeStatus" :key="placement.id" cols="12">
                            <v-icon small :color="placement.success ? 'green' : 'red'" class="pl-2">
                                {{ placement.success ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle' }}
                            </v-icon>
                            <b>{{ placement.seafarer.name }} {{ placement.seafarer.surname }}</b>
                            &nbsp;{{ placement.activity_display }}&nbsp;
                            <v-icon small color="black" class="pl-2">fas fa-arrow-right</v-icon>
                            &nbsp;{{ statusToChange }}&nbsp;
                            <span :class="placement.success ? 'green--text' : 'red--text'">
                                {{ placement.success ? $t('labels.success') : placement.message }}
                            </span>
                        </v-col>
                    </v-row>
                    <v-row class="mt-2">
                        <v-col cols="12" class="text-center">
                            <v-btn color="primary" class="white--text" @click.stop="closeResponseDialog">
                                {{ $t('buttons.close') }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Dropdown from './trinkets/Dropdown';
import CheckBox from './trinkets/CheckBox';
import DropdownMultiple from './trinkets/DropdownMultiple';
import DatePicker from '@/components/trinkets/DatePicker';
import PlacementCard from './PlacementCard.vue';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_STATUS_SELECTIONS = 10;
const MAX_BULK_PLAN = 30;

/**
 * Maps seafarer_status_id → human-readable label.
 * Single source of truth shared by the template's statusColumns and the
 * confirmChangeStatusClick label lookup.
 */
const SEAFARER_STATUS_LABELS = {
    1: 'Inactive',
    2: 'Sick Leave - No Contract',
    3: 'Available',
    4: 'On Leave',
    5: 'Travel to Vessel',
    6: 'Sick Leave',
    7: 'Training/visa/etc.',
    8: 'On board',
    9: 'Travel from Vessel',
};

/**
 * Column definitions for the five activity-based kanban columns.
 * Order here determines render order.
 */
const STATUS_COLUMNS = [
    {statusId: 5, label: 'labels.travelToVessel', color: '#FFFFFF'},
    {statusId: 7, label: 'labels.trainingVisaEtc', color: '#FFFFFF'},
    {statusId: 6, label: 'labels.sickLeave', color: '#FFE5E5'},
    {statusId: 8, label: 'labels.onBoard', color: '#ACFFB9'},
    {statusId: 9, label: 'labels.travelFromVessel', color: '#FFFFFF'},
];

// ─── Main component ───────────────────────────────────────────────────────────

export default {
    name: 'CrewList',

    components: {CheckBox, Dropdown, DropdownMultiple, DatePicker, PlacementCard},

    data() {
        return {
            // Maritime object selection
            selectedMaritimeObject: null,

            // Off-board filter
            selectedOffboard: 1,
            offboardSelect: [
                {value: 1, label: '7 days'},
                {value: 2, label: '15 days'},
                {value: 3, label: '30 days'},
                {value: 4, label: '90 days'},
            ],

            // Crew-list data
            crewListDataContractPlacements: [],
            crewListDataPlannedPlacements: [],
            crewListDataPlacementsNoContract: [],
            crewListOffBoard: [],

            // Bulk-plan dialog
            bulkPlanDialog: false,
            bulkPlanSeafarers: [],
            availableSeafarersForBulkPlan: [],
            componentKey: 1,
            bulkPlanDate: null,

            // Change-status flow
            changeStatusActive: false,
            lockTimer: null,
            selectedSeafarersForChangeStatus: [],
            changeStatusDialog: false,
            changeStatusTo: null,
            changeStatusDate: null,
            changeStatusInactiveReason: null,
            changeStatusVerifyDialog: false,
            changeStatusResponseDialog: false,
            statusToChange: '',
            dateForVerifyModal: '',
        };
    },

    computed: {
        /** Column definitions with translated labels. */
        statusColumns() {
            return STATUS_COLUMNS.map((col) => ({
                ...col,
                label: this.$t(col.label),
            }));
        },
    },

    watch: {
        selectedMaritimeObject() {
            if (this.changeStatusActive) {
                this.deactivateChangeStatus();
            }
            this.reloadData();
        },

        selectedOffboard() {
            this.reloadData();
        },

        changeStatusDate(val) {
            if (val) this.clampDateToToday();
        },

        bulkPlanSeafarers(val) {
            if (val.length > MAX_BULK_PLAN) {
                this.bulkPlanSeafarers = val.slice(0, MAX_BULK_PLAN);
                this.$notify(this.$t('alerts.cannotSelectMoreThanThirty'));
            }
        },
    },

    methods: {
        // ── Data ──────────────────────────────────────────────────────────────

        reloadData() {
            if (this.selectedMaritimeObject == null) return;

            this.$APICall(
                'get',
                'crew-list/' + this.selectedMaritimeObject,
                {selected_off_board: this.selectedOffboard},
                (data, success) => {
                    if (!success) return;
                    const d = JSON.parse(JSON.stringify(data));
                    this.crewListDataContractPlacements = d.contract_placements ? d.contract_placements : [];
                    this.crewListDataPlannedPlacements = d.planned_placements ? d.planned_placements : [];
                    this.crewListDataPlacementsNoContract = d.planned_placements_no_contract
                        ? d.planned_placements_no_contract
                        : [];
                    this.crewListOffBoard = d.offboard_placements ? d.offboard_placements : [];
                },
            );
        },

        getLastSavedMaritimeObject() {
            this.$APICall('get', 'crew-list-user-data', {}, (data, success) => {
                if (success) {
                    this.selectedMaritimeObject = JSON.parse(JSON.stringify(data)).maritime_object_id;
                }
            });
        },

        // ── Helpers ───────────────────────────────────────────────────────────

        /**
         * Filter contract placements by status ID for the kanban columns.
         */
        placementsByStatus(statusId) {
            return this.crewListDataContractPlacements.filter(
                (p) => p.activity && p.activity.seafarer_status_id === statusId,
            );
        },

        /**
         * Format a JS Date to 'DD/MM/YYYY'.
         */
        formatDisplayDate(date) {
            const d = new Date(date);
            return [
                String(d.getDate()).padStart(2, '0'),
                String(d.getMonth() + 1).padStart(2, '0'),
                d.getFullYear(),
            ].join('/');
        },

        /**
         * Format a date string to 'YYYY-MM-DD' (zero-padded).
         */
        formatIsoDate(date) {
            const d = new Date(date);
            return [
                d.getFullYear(),
                String(d.getMonth() + 1).padStart(2, '0'),
                String(d.getDate()).padStart(2, '0'),
            ].join('-');
        },

        openPlacement(placementId) {
            const route = this.$router.resolve('/placements/' + placementId);
            window.open(route.href, '_blank');
        },

        // ── Change-status flow ────────────────────────────────────────────────

        toggleChangeStatus() {
            this.reloadData();
            if (this.changeStatusActive) {
                this.deactivateChangeStatus();
            } else {
                this.activateChangeStatus();
            }
        },

        activateChangeStatus() {
            this.$setLock(this, 'crew-list|' + this.selectedMaritimeObject, (data) => {
                if (data.success) {
                    this.selectedSeafarersForChangeStatus = [];
                    this.changeStatusActive = true;
                    this.lockTimer = setInterval(() => {
                        this.$setLock(this, 'crew-list|' + this.selectedMaritimeObject);
                    }, 10000);
                } else {
                    this.$alert(
                        this.$t('alerts.recordLocked')
                            .split('%name%')
                            .join(data.locked_by.first_name + ' ' + data.locked_by.last_name),
                    );
                }
            });
        },

        deactivateChangeStatus() {
            this.changeStatusActive = false;
            this.selectedSeafarersForChangeStatus = [];
            if (this.lockTimer !== null) {
                clearInterval(this.lockTimer);
                this.lockTimer = null;
            }
            this.$removeLock(this, 'crew-list|' + this.selectedMaritimeObject);
        },

        /**
         * Toggle a placement in/out of the selection list.
         */
        addSeafarerToChangeStatusList(placement) {
            const index = this.selectedSeafarersForChangeStatus.findIndex((x) => x.id === placement.id);

            if (index !== -1) {
                // Already selected → deselect.
                this.selectedSeafarersForChangeStatus.splice(index, 1);
                placement.selected = false;
                return;
            }

            if (this.selectedSeafarersForChangeStatus.length >= MAX_STATUS_SELECTIONS) {
                placement.selected = false;
                this.$notify(this.$t('alerts.cannotSelectMoreThanTen'));
                return;
            }

            this.selectedSeafarersForChangeStatus.push(placement);
        },

        applyChangesClick() {
            if (this.selectedSeafarersForChangeStatus.length < 1) {
                this.$notify(this.$t('alerts.pleaseSelectSeafarers'));
                return;
            }
            this.changeStatusDialog = true;
        },

        clampDateToToday() {
            const today = new Date();
            const selectedDate = new Date(this.changeStatusDate);
            if (today < selectedDate) {
                this.changeStatusDate = this.formatIsoDate(today);
                this.$notify(this.$t('alerts.cannotSelectDateInFuture'));
            }
        },

        confirmChangeStatusClick() {
            const missing = [];
            if (this.changeStatusTo == null) missing.push(this.$t('fields.status'));
            if (this.changeStatusDate == null) missing.push(this.$t('fields.date'));
            if (this.changeStatusTo === 1 && this.changeStatusInactiveReason == null) {
                missing.push(this.$t('fields.reason'));
            }

            if (missing.length) {
                this.$alert('Missing fields: <br/>' + missing.join('<br/>'));
                return;
            }

            this.statusToChange = SEAFARER_STATUS_LABELS[this.changeStatusTo]
                ? SEAFARER_STATUS_LABELS[this.changeStatusTo]
                : 'Unknown';
            this.dateForVerifyModal = this.formatDisplayDate(this.changeStatusDate);
            this.changeStatusVerifyDialog = true;
        },

        closeChangeStatusDialog() {
            this.changeStatusTo = null;
            this.changeStatusDate = null;
            this.changeStatusInactiveReason = null;
            this.changeStatusDialog = false;
        },

        confirmChangeStatusVerifyDialog() {
            const isOffVessel = [1, 2, 3, 4].includes(this.changeStatusTo);
            const alertKey = isOffVessel
                ? 'alerts.completePlacementCrewlistChangeStatus'
                : 'alerts.changeStatusOnPlacementsCrewList';
            const apiMethod = isOffVessel
                ? this.completePlacementsAndChangeStatusesDatabase
                : this.changeStatusesDatabase;

            this.$verify(this.$t(alertKey), (result) => {
                if (result) apiMethod.call(this);
            });
        },

        closeChangeStatusVerifyDialog() {
            this.changeStatusVerifyDialog = false;
        },

        closeResponseDialog() {
            this.changeStatusResponseDialog = false;
            this.selectedSeafarersForChangeStatus = [];
        },

        changeStatusesDatabase() {
            this.$APICall(
                'post',
                'change-status-placements-crew-list',
                {
                    date: this.changeStatusDate,
                    placements: this.selectedSeafarersForChangeStatus,
                    status: this.changeStatusTo,
                },
                (data, success) => {
                    if (success) {
                        this.onChangeStatusSuccess(data);
                    } else {
                        this.$alert(this.$t('alerts.basicError'));
                    }
                },
            );
        },

        completePlacementsAndChangeStatusesDatabase() {
            this.$APICall(
                'post',
                'complete-placements-crew-list',
                {
                    date: this.changeStatusDate,
                    placements: this.selectedSeafarersForChangeStatus,
                    status: this.changeStatusTo,
                    inactive_reason_id: this.changeStatusTo === 1 ? this.changeStatusInactiveReason : null,
                },
                (data, success) => {
                    if (success) {
                        this.onChangeStatusSuccess(data);
                    } else {
                        this.$alert(data && data.message ? data.message : this.$t('alerts.basicError'));
                    }
                },
            );
        },

        onChangeStatusSuccess(data) {
            this.selectedSeafarersForChangeStatus = data.placements;
            this.changeStatusDialog = false;
            this.changeStatusVerifyDialog = false;
            this.changeStatusResponseDialog = true;
            this.closeChangeStatusDialog();
            this.reloadData();
        },

        // ── Bulk-plan flow ────────────────────────────────────────────────────

        bulkPlanClick() {
            if (this.selectedMaritimeObject == null) {
                this.$alert(this.$t('alerts.selectMaritimeObject'));
                return;
            }

            this.bulkPlanDialog = true;
            this.$APICall('get', 'available-seafarers-for-bulk-plan', {}, (data, success) => {
                if (success) {
                    this.availableSeafarersForBulkPlan = JSON.parse(JSON.stringify(data));
                    this.componentKey++;
                }
            });
        },

        bulkPlanDialogClick() {
            const missing = [];
            if (!this.bulkPlanSeafarers.length) missing.push(this.$t('fields.seafarers'));
            if (!this.bulkPlanDate) missing.push(this.$t('fields.date'));

            if (missing.length) {
                this.$alert('Missing fields: <br/>' + missing.join('<br/>'));
                return;
            }

            this.$APICall(
                'post',
                'create-planned-placements/' + this.selectedMaritimeObject,
                {date: this.bulkPlanDate, seafarers: this.bulkPlanSeafarers},
                (data, success) => {
                    if (success) {
                        if (data && data.skipped_deceased && data.skipped_deceased.length) {
                            this.$alert(
                                this.$t('alerts.bulkPlanSkippedDeceased') +
                                    '<br/>' +
                                    data.skipped_deceased.join('<br/>'),
                            );
                        }
                        this.closeBulkPlanDialog();
                        this.reloadData();
                    } else {
                        this.$notify(this.$t('alerts.basicError'));
                    }
                },
            );
        },

        closeBulkPlanDialog() {
            this.bulkPlanDialog = false;
            this.bulkPlanSeafarers = [];
            this.bulkPlanDate = null;
        },
    },

    beforeRouteLeave(to, from, next) {
        if (!this.changeStatusActive) {
            this.deactivateChangeStatus();
            return next(true);
        }

        this.$verify(this.$t('alerts.leaveQuestion'), (result) => {
            if (result) {
                this.deactivateChangeStatus();
                next(true);
            } else {
                next(false);
            }
        });
    },

    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
            return;
        }
        this.getLastSavedMaritimeObject();
    },
};
</script>

<style scoped>
html {
    overflow: hidden !important;
}

.v-card {
    display: flex !important;
    flex-direction: column;
}

.v-card__text {
    flex-grow: 1;
    overflow: auto;
}

.collTitle {
    font-size: 1.1em;
    padding-top: 7px;
    padding-bottom: 7px;
}

.nameLabelCard {
    font-size: 1.2em;
    font-weight: bold;
    padding-bottom: 0;
    padding-left: 0;
}

.dateLabelCard {
    padding: 0 0 0 7px;
    font-size: 0.9em;
}

.rankLabelCard {
    font-size: 0.9em;
    padding: 0;
    margin-left: 0;
}
</style>
